import { useFeatureGateContext } from './FeatureGateProvider'
import type { FeVisibility } from '@/services/api/billing/useGetBillingSubscription'

export interface ResolvedFeature {
  lookupKey: string
  name: string
  available: boolean
  value: string | null
  feVisibility: FeVisibility
  upsellModal: boolean
}

/**
 * Resolve a plan feature by lookup_key.
 *
 * While the plan catalogue is still loading, features are reported as available
 * so the UI does not flash locked states on every navigation.
 */
export function useFeature(lookupKey: string): ResolvedFeature {
  const { features, loaded } = useFeatureGateContext()
  const feature = features[lookupKey]

  if (!feature) {
    return {
      lookupKey,
      name: lookupKey,
      available: !loaded, // optimistic until loaded, then unknown key => unavailable
      value: null,
      feVisibility: 'lock',
      upsellModal: true,
    }
  }

  return {
    lookupKey,
    name: feature.name,
    available: !loaded || feature.available,
    value: feature.value,
    feVisibility: feature.fe_visibility,
    upsellModal: feature.upsell_modal,
  }
}

export function useHasFeature(lookupKey: string): boolean {
  return useFeature(lookupKey).available
}

export function useFeatureValue<T extends string | number>(
  lookupKey: string,
  fallback: T,
): T {
  const { value, available } = useFeature(lookupKey)
  if (!available || value === null) return fallback
  return (typeof fallback === 'number' ? Number(value) : value) as T
}

export function useFeaturesLoaded(): boolean {
  return useFeatureGateContext().loaded
}
